import { useField } from 'vee-validate'
import { computed, type Ref } from 'vue'
import type { CustomerInformationSchemaType } from '~/components/Checkout/Sections/CustomerInformation.vue'
import type { BillingAddressSchemaTyped } from '~/components/Checkout/Sections/BillingAddress.vue'
import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'

export type PaymentMethodInput = {
    customerInformation: CheckoutCustomerInformation
    addressInformation: CheckoutAddressInformation
}

export type CheckoutCustomerInformation = {
    firstName: string
    lastName: string
    email: string
    phone: string
}

export type CheckoutAddressInformation = {
    address1: string
    address2: string
    city: string
    state: string
    postal: string
    country: string
}

export function usePaymentMethodInput() {
    const { customer, customerInput } = useCustomerField()
    const { address, addressInput } = useAddressField()
    const paymentMethodInput = computed<PaymentMethodInput>(() => {
        return {
            customerInformation: customerInput.value,
            addressInformation: addressInput.value
        }
    })

    return {
        paymentMethodInput,
        customer,
        customerInput,
        address,
        addressInput
    }
}

export function useCustomerField() {
    const { value: customer } = useField<CustomerInformationSchemaType>(
        'customer',
        '',
        {
            validateOnValueUpdate: false,
            // Don't reset the values
            // see: https://github.com/logaretm/vee-validate/issues/4830
            keepValueOnUnmount: true,
            initialValue: undefined
        }
    )

    const customerInput = computed(() => {
        return {
            firstName: customer.value.firstName,
            lastName: customer.value.lastName,
            email: customer.value.email,
            phone: customer.value.mobilePhone?.number ?? ''
        }
    })

    return {
        customer,
        customerInput
    }
}

export function useAddressField() {
    const { value: address } = useField<BillingAddressSchemaTyped>(
        'address',
        '',
        {
            validateOnValueUpdate: false,
            // Don't reset the values
            // see: https://github.com/logaretm/vee-validate/issues/4830
            keepValueOnUnmount: true,
            initialValue: undefined
        }
    )

    const addressInput = computed(() => {
        return {
            address1: address.value.address1 ?? '',
            address2: address.value.address2 ?? '',
            city: address.value.city ?? '',
            state: address.value.state ?? '',
            postal: address.value.postal ?? '',
            country: address.value.country ?? ''
        }
    })

    return {
        address,
        addressInput
    }
}

export const PaymentMethodInputPlanContractFragment = graphql(/** GraphQL */ `
    fragment PaymentMethodInputPlanContractFragment on PlanContract {
        terms {
            customer {
                firstName
                lastName
                email
                mobilePhone
            }
        }
    }
`)

export function useGuestPaymentMethodInput(
    contract: Ref<FragmentType<typeof PaymentMethodInputPlanContractFragment>>
) {
    const contractData = computed(() => {
        return getFragmentData(
            PaymentMethodInputPlanContractFragment,
            contract.value
        )
    })

    const { address, addressInput } = useAddressField()
    const paymentMethodInput = computed<PaymentMethodInput>(() => {
        // We have to have customer information by this time to make the payment source
        if (contractData.value.terms.customer === null) {
            throw new Error()
        }

        return {
            customerInformation: {
                firstName: contractData.value.terms.customer.firstName,
                lastName: contractData.value.terms.customer.lastName,
                email: contractData.value.terms.customer.email,
                phone: contractData.value.terms.customer.mobilePhone
            },
            addressInformation: addressInput.value
        }
    })

    return {
        paymentMethodInput,
        address,
        addressInput
    }
}
